import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  theme,
} from "antd";
import {
  EyeOutlined,
  PlusOutlined,
  PrinterOutlined,
  SearchOutlined,
  ShopOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, formatNumber, toApiNumber } from "../../../utils/formatters";
import { parseLocalizedName } from "../../../utils/localization";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { T } from "../../../shared/ui/PageShell";
import useTablePagination from "../../../shared/ui/useTablePagination";
import {
  DATE_FILTER_PRESET_DEFS,
  getEnabledDateFilterPresets,
  resolveDatePreset,
} from "../../../shared/lib/dateFilterPresets";
import FarmerFinanceTabs, { useFarmerFinanceTabAccess } from "../components/FarmerFinanceTabs";
import AddVendorExpenseModal from "../components/AddVendorExpenseModal";
import FarmerExpenses from "./FarmerExpenses";
import FarmerPayments from "./FarmerPayments";
import { fetchExpenseTypes, expenseTypeLabelMap } from "../lib/expenseTypes";
import { printFarmerSettlementStatement } from "../lib/printFarmerSettlement";
import {
  vendorFinanceFromConsignment,
  vendorItemAmount,
} from "../lib/vendorFinance";
import { openPrintWindow } from "../../../shared/lib/printHtml";
import { getPrintedByInfo } from "../../../shared/lib/session";
import "./FarmerSettlementWorkspace.css";

const money = (value) => toApiNumber(value, 0);
const VALID_TABS = new Set(["overview", "charges", "payments"]);

export default function FarmerSettlementWorkspace() {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { allowedTabs, defaultTab, canPayAndPrint, canAddExpenses, canViewExpenses } = useFarmerFinanceTabAccess();
  const printedBy = useMemo(() => getPrintedByInfo(), []);
  const { pagination: saleItemsPagination, resetPage: resetSaleItemsPage } = useTablePagination();
  const { pagination: expensePagination, resetPage: resetExpensePage } = useTablePagination();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = VALID_TABS.has(searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const activeTab = allowedTabs.includes(requestedTab) ? requestedTab : defaultTab;
  const setActiveTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === "overview") next.delete("tab");
    else next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (requestedTab !== activeTab && allowedTabs.length) {
      const next = new URLSearchParams(searchParams);
      if (activeTab === "overview") next.delete("tab");
      else next.set("tab", activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [requestedTab, activeTab, allowedTabs.length, searchParams, setSearchParams]);

  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState(null);
  const [productIds, setProductIds] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [range, setRange] = useState([null, null]);
  const [presetKey, setPresetKey] = useState(null);
  const [report, setReport] = useState(null);
  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleAmount, setSettleAmount] = useState(0);
  const [settleNote, setSettleNote] = useState("");
  const [settling, setSettling] = useState(false);
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [selectedSaleIds, setSelectedSaleIds] = useState([]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState([]);
  const [scopedReport, setScopedReport] = useState(null);
  const [loadingScoped, setLoadingScoped] = useState(false);
  const requestIdRef = useRef(0);
  const scopedRequestRef = useRef(0);
  const mainCode = systemConfig?.currency_code || "IQD";

  const selectedVendor = useMemo(
    () => vendors.find((vendor) => Number(vendor.id) === Number(vendorId)) || null,
    [vendorId, vendors]
  );

  const presetOptions = useMemo(() => {
    const enabled = getEnabledDateFilterPresets(systemConfig);
    return DATE_FILTER_PRESET_DEFS
      .filter((preset) => enabled.includes(preset.key))
      .map((preset) => ({
        value: preset.key,
        label: t(preset.labelKey, preset.defaultLabel),
      }));
  }, [systemConfig, t]);

  const applyPreset = (key) => {
    setPresetKey(key);
    setRange(key ? resolveDatePreset(key, { withTime: true }) : [null, null]);
  };

  const changeRangeBoundary = (index, value) => {
    setPresetKey(null);
    setRange((current) => {
      const next = Array.isArray(current) ? [...current] : [null, null];
      next[index] = value;
      return next;
    });
  };

  const buildParams = useCallback((nextProducts = productIds, nextRange = range, nextOrderIds = null, nextExpenseIds = null) => {
    const params = {};
    if (nextRange?.[0]) params.date_from = dayjs(nextRange[0]).format("YYYY-MM-DD HH:mm:ss");
    if (nextRange?.[1]) params.date_to = dayjs(nextRange[1]).format("YYYY-MM-DD HH:mm:ss");
    const ids = Array.isArray(nextProducts) ? nextProducts.filter(Boolean) : [];
    if (ids.length === 1) params.product_id = ids[0];
    if (ids.length > 1) params.product_ids = ids;
    const orderIds = Array.isArray(nextOrderIds)
      ? [...new Set(nextOrderIds.map(Number).filter((id) => Number.isFinite(id) && id > 0))]
      : [];
    if (orderIds.length) params.order_ids = orderIds;
    const expenseIds = Array.isArray(nextExpenseIds)
      ? [...new Set(nextExpenseIds.map(Number).filter((id) => Number.isFinite(id) && id > 0))]
      : [];
    if (expenseIds.length) params.expense_ids = expenseIds;
    return params;
  }, [productIds, range]);

  const loadReport = useCallback(async (
    nextVendorId = vendorId,
    nextProducts = productIds,
    nextRange = range,
  ) => {
    if (!nextVendorId) {
      setReport(null);
      setScopedReport(null);
      setSelectedSaleIds([]);
      setSelectedExpenseIds([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoadingReport(true);
    setSelectedSaleIds([]);
    setSelectedExpenseIds([]);
    setScopedReport(null);
    try {
      const response = await apiClient.get(`/pos/vendor-consignment/${nextVendorId}`, {
        params: buildParams(nextProducts, nextRange, null),
      });
      if (requestId !== requestIdRef.current) return;
      const data = response.data || {};
      setReport(data);
      const incoming = Array.isArray(data.products) ? data.products : [];
      setProductOptions((current) => {
        const merged = new Map((current || []).map((product) => [Number(product.product_id), product]));
        incoming.forEach((product) => merged.set(Number(product.product_id), product));
        return Array.from(merged.values());
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setReport(null);
      message.error(error?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      if (requestId === requestIdRef.current) setLoadingReport(false);
    }
  }, [buildParams, productIds, range, t, vendorId]);

  // When sales and/or expenses are checked, reload money totals scoped to exactly
  // that selection — a live preview of what will actually be cleared.
  useEffect(() => {
    if (!vendorId || (!selectedSaleIds.length && !selectedExpenseIds.length)) {
      setScopedReport(null);
      setLoadingScoped(false);
      return undefined;
    }
    const requestId = ++scopedRequestRef.current;
    setLoadingScoped(true);
    const timer = setTimeout(async () => {
      try {
        const response = await apiClient.get(`/pos/vendor-consignment/${vendorId}`, {
          params: buildParams(productIds, range, selectedSaleIds, selectedExpenseIds),
        });
        if (requestId !== scopedRequestRef.current) return;
        setScopedReport(response.data || null);
      } catch {
        if (requestId !== scopedRequestRef.current) return;
        setScopedReport(null);
      } finally {
        if (requestId === scopedRequestRef.current) setLoadingScoped(false);
      }
    }, 200);
    return () => {
      clearTimeout(timer);
    };
  }, [vendorId, selectedSaleIds, selectedExpenseIds, productIds, range, buildParams]);

  const loadBase = useCallback(async () => {
    setLoadingBase(true);
    try {
      const [vendorsResponse, config, types] = await Promise.all([
        apiClient.get("/vendors"),
        fetchSystemConfig(),
        fetchExpenseTypes(),
      ]);
      setVendors(Array.isArray(vendorsResponse.data) ? vendorsResponse.data : []);
      setSystemConfig(config);
      setExpenseTypes(types);
    } catch (error) {
      message.error(error?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setLoadingBase(false);
    }
  }, [t]);

  useEffect(() => {
    loadBase();
  }, [loadBase]);

  // Pay-farmer list links use ?vendor=ID → open Reports with that farmer selected.
  const vendorParam = searchParams.get("vendor") || searchParams.get("vendor_id");
  useEffect(() => {
    if (!vendorParam || !vendors.length) return;
    const id = Number(vendorParam);
    if (!Number.isFinite(id) || !vendors.some((v) => Number(v.id) === id)) return;
    if (Number(vendorId) === id) return;

    setVendorId(id);
    setProductIds([]);
    setProductOptions([]);
    setReport(null);
    loadReport(id, [], range);

    // Leave payments/charges tabs: land on Reports with farmer selected.
    if (searchParams.get("tab")) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      next.set("vendor", String(id));
      setSearchParams(next, { replace: true });
    }
  }, [vendorParam, vendors, vendorId, range, loadReport, searchParams, setSearchParams]);

  const chooseVendor = (nextId) => {
    const value = nextId ?? null;
    setVendorId(value);
    setProductIds([]);
    setProductOptions([]);
    setReport(null);
    setScopedReport(null);
    setSelectedSaleIds([]);
    setSelectedExpenseIds([]);
    resetSaleItemsPage();
    resetExpensePage();
    if (value) loadReport(value, [], range);

    const next = new URLSearchParams(searchParams);
    if (value) next.set("vendor", String(value));
    else next.delete("vendor");
    // Keep current tab when picking from the Reports dropdown.
    setSearchParams(next, { replace: true });
  };

  const refresh = () => {
    resetSaleItemsPage();
    resetExpensePage();
    loadReport(vendorId, productIds, range);
  };

  const openSettlement = () => {
    if (!canPayAndPrint || !report || !hasActivity) return;
    const paySource = (selectedSaleIds.length || selectedExpenseIds.length) && scopedReport ? scopedReport : report;
    const payRemaining = vendorFinanceFromConsignment(paySource).remaining;
    setSettleAmount(Math.round(payRemaining * 100) / 100);
    setSettleNote("");
    setSettleOpen(true);
  };

  const refreshVendors = useCallback(async () => {
    try {
      const response = await apiClient.get("/vendors");
      setVendors(Array.isArray(response.data) ? response.data : []);
    } catch {
      /* keep current list */
    }
  }, []);

  const printArchiveOrReport = async (archivePayload = null, printWin = null, noteOverride = null) => {
    if (archivePayload?.id) {
      try {
        const full = await apiClient.get(`/vendor-settlement-archives/${archivePayload.id}`);
        const snap = full.data?.snapshot || {};
        await printFarmerSettlementStatement({
          consignment: snap,
          vendor: {
            id: full.data.vendor_id || selectedVendor?.id,
            name: full.data.vendor_name || selectedVendor?.name,
            phone: full.data.vendor_phone || selectedVendor?.phone,
            consignment_owed: snap.total_debt,
          },
          systemConfig,
          language: i18n.language,
          t,
          dateFrom: full.data.date_from || snap.date_from,
          dateTo: full.data.date_to || snap.date_to,
          settledAt: full.data.settled_at,
          archiveId: full.data.id,
          expenseTypes,
          previousDebt: snap.previous_debt,
          totalDebt: snap.total_debt,
          documentNo: full.data.document_no,
          note: noteOverride || full.data.note || snap.note || snap.settlement_note || null,
          printedBy,
          printWin,
        });
        return;
      } catch {
        /* fall through to live report print */
      }
    }
    if (!report || !selectedVendor) return;
    const printReport = (selectedSaleIds.length || selectedExpenseIds.length) && scopedReport ? scopedReport : report;
    await printFarmerSettlementStatement({
      consignment: printReport,
      vendor: selectedVendor,
      systemConfig,
      language: i18n.language,
      t,
      dateFrom: range?.[0]?.format("YYYY-MM-DD HH:mm:ss"),
      dateTo: range?.[1]?.format("YYYY-MM-DD HH:mm:ss"),
      expenseTypes,
      note: noteOverride || settleNote || null,
      printedBy,
      printWin,
    });
  };

  const submitSettlement = async () => {
    const amount = Math.round(money(settleAmount) * 100) / 100;
    if (amount < 0) {
      message.error(t("vendorsPaymentInvalid", "Enter a valid amount"));
      return;
    }
    const isPartial = remaining > 0.01 && amount < remaining - 0.01;
    const settleNoteText = settleNote || t("vendorSettleNote", "Settlement clear / archive");

    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: isPartial
          ? t("vendorPayPartialConfirmTitle", "Confirm partial payment?")
          : t("vendorClearConfirmTitle", "Confirm clear & archive?"),
        content: isPartial
          ? t("vendorPayPartialConfirmBody", "Record {{amount}} as a partial payment? The account will stay open.", {
            amount: formatCurrency(amount, mainCode),
          })
          : t("vendorClearConfirmBody", "Clear {{amount}} and archive this settlement? This cannot be undone.", {
            amount: formatCurrency(amount, mainCode),
          }),
        okText: t("confirm", "Confirm"),
        cancelText: t("cancel", "Cancel"),
        okType: isPartial ? "primary" : "danger",
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    // Open print window on this click (before await) so Apple Safari preview works.
    const printWin = openPrintWindow();
    const settledVendorId = vendorId;
    setSettling(true);
    setPrinting(true);
    try {
      const body = {
        ...buildParams(
          productIds,
          range,
          selectedSaleIds.length ? selectedSaleIds : null,
          selectedExpenseIds.length ? selectedExpenseIds : null
        ),
        amount,
        note: settleNoteText,
      };
      const response = await apiClient.post(`/vendors/${settledVendorId}/settle-consignment`, body);
      const archived = response.data?.archived !== false && Boolean(response.data?.archive?.id);
      if (archived) {
        message.success(
          selectedSaleIds.length
            ? t("vendorSettledSelectedArchived", "Selected sales cleared and archived")
            : t("vendorSettledArchived", "Cleared and moved to archive")
        );
      } else {
        message.success(t("vendorPartialPaid", "Partial payment recorded — account stays open"));
      }
      setSettleOpen(false);
      setSelectedSaleIds([]);
      setSelectedExpenseIds([]);
      setScopedReport(null);
      try {
        await printArchiveOrReport(response.data?.archive || null, printWin, settleNoteText);
      } catch (printError) {
        try {
          if (printWin && !printWin.closed) printWin.close();
        } catch {
          /* ignore */
        }
        message.error(printError?.message || t("printFailed", "Could not print"));
      }
      setProductIds([]);
      setProductOptions([]);
      await refreshVendors();
      await loadReport(settledVendorId, [], range);
    } catch (error) {
      try {
        if (printWin && !printWin.closed) printWin.close();
      } catch {
        /* ignore */
      }
      message.error(error?.response?.data?.message || t("vendorsPaymentFailed", "Payment failed"));
    } finally {
      setSettling(false);
      setPrinting(false);
    }
  };

  const expenseLabels = useMemo(() => expenseTypeLabelMap(expenseTypes), [expenseTypes]);
  const hasScopedSelection = selectedSaleIds.length > 0 || selectedExpenseIds.length > 0;
  const activeReport = hasScopedSelection && scopedReport ? scopedReport : report;
  // Payback without commission: product gross − expenses − paid.
  const finance = useMemo(() => vendorFinanceFromConsignment(activeReport || {}), [activeReport]);
  const remaining = finance.remaining;
  const netOwed = finance.netOwed;
  const paid = finance.paid;
  const expensesTotal = finance.expenses;
  const soldTotal = finance.gross;
  const orderScoped = selectedSaleIds.length > 0;
  // Physical totals for the selected period (net after returns).
  const physicalTotals = useMemo(() => {
    const products = Array.isArray(activeReport?.products) ? activeReport.products : [];
    if (products.length) {
      return products.reduce(
        (acc, p) => ({
          qty: acc.qty + money(p.cases_sold),
          weight: acc.weight + money(p.kg_sold),
        }),
        { qty: 0, weight: 0 }
      );
    }
    return { qty: 0, weight: 0 };
  }, [activeReport]);

  const settlePreview = useMemo(() => {
    const sales = Array.isArray(activeReport?.sales) ? activeReport.sales : [];
    const openSales = sales.filter((sale) => !sale.settled);
    const saleDocs = openSales
      .map((sale) => sale.document_no || `#${sale.id}`)
      .filter(Boolean);
    const chargeCount = (activeReport?.expenses || []).filter((row) => !row.cancelled_at).length;
    const payNow = Math.round(money(settleAmount) * 100) / 100;
    const isPartial = remaining > 0.01 && payNow < remaining - 0.01;
    return {
      saleCount: openSales.length,
      saleDocs,
      qty: physicalTotals.qty,
      weight: physicalTotals.weight,
      gross: soldTotal,
      charges: expensesTotal,
      chargeCount,
      paid,
      net: netOwed,
      remaining,
      payNow,
      isPartial,
      willArchive: !isPartial,
      orderScoped,
    };
  }, [
    activeReport,
    physicalTotals,
    soldTotal,
    expensesTotal,
    paid,
    netOwed,
    remaining,
    settleAmount,
    orderScoped,
  ]);

  // Include migrated / manual owed balances (open payments, no sales yet).
  const hasActivity = Boolean(
    report?.sales?.length
    || report?.products?.length
    || report?.expenses?.length
    || remaining > 0.01
    || Math.abs(paid) > 0.01
    || (orderScoped && (scopedReport?.sales?.length || remaining > 0.01))
  );

  const saleItemRows = useMemo(() => {
    const rows = [];
    (report?.sales || []).forEach((sale) => {
      const items = Array.isArray(sale.items) && sale.items.length
        ? sale.items
        : [{
          product: null,
          qty: null,
          quantity: null,
          weight: null,
          unit_price: null,
          subtotal: sale.gross_sold ?? sale.gross_total,
          commission: sale.commission,
          net: sale.net_owed,
          sold_qty: null,
          sold_weight: null,
        }];
      items.forEach((item, index) => {
        const qty = money(item.sold_qty ?? item.qty ?? item.quantity);
        const weight = money(item.sold_weight ?? item.weight);
        const lineAmount = vendorItemAmount(item);
        rows.push({
          key: `${sale.id}-${index}`,
          saleId: sale.id,
          documentNo: sale.document_no || `#${sale.id}`,
          date: sale.created_at,
          customer: sale.customer?.name || "—",
          product: item.product
            ? parseLocalizedName(item.product.name, i18n.language, `#${item.product.id}`)
            : t("product", "Product"),
          qty,
          weight,
          unit: weight > 0 ? "KG" : "PCS",
          price: money(item.unit_price),
          total: lineAmount,
          net: lineAmount,
          settled: Boolean(item.settlement_archive_id || sale.settled || sale.settlement_archive_id),
        });
      });
    });
    return rows;
  }, [i18n.language, report, t]);

  const expenseRows = useMemo(
    () => (activeReport?.expenses || [])
      .filter((row) => !row.cancelled_at)
      .map((row, index) => ({
        ...row,
        key: row.id ?? index,
        label: expenseLabels[row.type] || row.type,
      })),
    [expenseLabels, activeReport]
  );

  const openSaleIds = useMemo(
    () => [...new Set(
      saleItemRows.filter((row) => !row.settled).map((row) => Number(row.saleId))
    )],
    [saleItemRows]
  );

  const saleRowSelection = canPayAndPrint ? {
    selectedRowKeys: saleItemRows
      .filter((row) => selectedSaleIds.includes(Number(row.saleId)) && !row.settled)
      .map((row) => row.key),
    onSelect: (record, selected) => {
      const id = Number(record.saleId);
      if (!Number.isFinite(id) || record.settled) return;
      setSelectedSaleIds((prev) => {
        if (selected) return [...new Set([...prev.map(Number), id])];
        return prev.map(Number).filter((value) => value !== id);
      });
    },
    onSelectAll: (selected) => {
      setSelectedSaleIds(selected ? openSaleIds : []);
    },
    getCheckboxProps: (record) => ({
      disabled: Boolean(record.settled),
    }),
  } : undefined;

  const openExpenseIds = useMemo(
    () => [...new Set(expenseRows.map((row) => Number(row.id)).filter((id) => Number.isFinite(id)))],
    [expenseRows]
  );

  const expenseRowSelection = canPayAndPrint ? {
    selectedRowKeys: expenseRows
      .filter((row) => selectedExpenseIds.includes(Number(row.id)))
      .map((row) => row.key),
    onSelect: (record) => {
      const id = Number(record.id);
      if (!Number.isFinite(id)) return;
      setSelectedExpenseIds((prev) => {
        const already = prev.map(Number).includes(id);
        if (already) return prev.map(Number).filter((value) => value !== id);
        return [...new Set([...prev.map(Number), id])];
      });
    },
    onSelectAll: (selected) => {
      setSelectedExpenseIds(selected ? openExpenseIds : []);
    },
  } : undefined;

  const saleColumns = [
    {
      title: t("date", "Date"),
      dataIndex: "date",
      width: 110,
      render: (value) => value ? dayjs(value).format("YYYY-MM-DD") : "—",
    },
    {
      title: t("farmerSaleNo", "Sale #"),
      dataIndex: "documentNo",
      width: 110,
      render: (value, row) => (
        <Link to={`/orders/${row.saleId}/view`} style={{ fontWeight: 700 }}>
          {value}
        </Link>
      ),
    },
    {
      title: t("product", "Product"),
      dataIndex: "product",
      render: (value, row) => (
        <span style={{ fontWeight: 700 }}>
          {value}
          {row.settled ? (
            <>
              {" "}
              <Tag color="purple">{t("archived", "Archived")}</Tag>
            </>
          ) : null}
        </span>
      ),
    },
    {
      title: t("buyer", "Buyer"),
      dataIndex: "customer",
    },
    {
      title: t("unit", "Unit"),
      dataIndex: "unit",
      width: 80,
      align: "center",
      render: (value) => <Tag className="fs-unit-tag" color={value === "KG" ? "green" : "blue"}>{value === "PCS" ? t("pieces", "PCS") : value}</Tag>,
    },
    {
      title: t("weight", "Weight"),
      dataIndex: "weight",
      width: 90,
      align: "center",
      render: (value) => value > 0 ? formatNumber(value) : "—",
    },
    {
      title: t("quantity", "Qty"),
      dataIndex: "qty",
      width: 70,
      align: "center",
      render: (value) => value > 0 ? formatNumber(value) : "—",
    },
    {
      title: t("price", "Price"),
      dataIndex: "price",
      align: "right",
      render: (value) => value > 0 ? formatCurrency(value, mainCode) : "—",
    },
    {
      title: t("total", "Total"),
      dataIndex: "total",
      align: "right",
      render: (value) => formatCurrency(value, mainCode),
    },
    {
      title: t("amountPaidShort", "Paid"),
      key: "paid",
      align: "right",
      render: () => formatCurrency(0, mainCode),
    },
    {
      title: t("netUnpaid", "Net unpaid"),
      dataIndex: "net",
      align: "right",
      render: (value) => <b style={{ color: "#16a34a" }}>{formatCurrency(value, mainCode)}</b>,
    },
  ];

  const expenseColumns = [
    {
      title: t("type", "Type"),
      dataIndex: "label",
      render: (value) => <Tag color="orange">{value}</Tag>,
    },
    {
      title: t("product", "Product"),
      key: "product",
      render: (_, row) => row.product_id
        ? parseLocalizedName(row.product_name, i18n.language, `#${row.product_id}`)
        : <span style={{ color: T.muted }}>{t("vendorExpenseFullAccount", "All products")}</span>,
    },
    {
      title: t("count", "Count"),
      key: "count",
      width: 80,
      align: "center",
      render: () => "1x",
    },
    {
      title: t("amount", "Amount"),
      dataIndex: "amount",
      align: "right",
      render: (value) => <b style={{ color: "#f59e0b" }}>−{formatCurrency(value, mainCode)}</b>,
    },
    {
      title: t("note", "Note"),
      dataIndex: "note",
      render: (value) => value || "—",
    },
    {
      title: t("date", "Date"),
      dataIndex: "created_at",
      width: 140,
      render: (value) => value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "—",
    },
    {
      title: t("status", "Status"),
      key: "status",
      width: 100,
      align: "center",
      render: (_, row) => (
        row.settlement_archive_id
          ? <Tag color="purple">{t("archived", "Archived")}</Tag>
          : <Tag color="green">{t("active", "Active")}</Tag>
      ),
    },
  ];

  const themeVars = {
    "--fs-page": token.colorBgLayout,
    "--fs-card": token.colorBgContainer,
    "--fs-card-alt": token.colorFillQuaternary,
    "--fs-border": token.colorBorderSecondary,
    "--fs-text": token.colorText,
    "--fs-muted": token.colorTextSecondary,
  };

  return (
    <div className="farmer-settlement-workspace" style={themeVars}>
      <FarmerFinanceTabs activeKey={activeTab} onChange={setActiveTab} />

      {activeTab === "charges" && (
        <div className="fs-pane fs-pane--embed">
          <FarmerExpenses embedded />
        </div>
      )}
      {activeTab === "payments" && (
        <div className="fs-pane fs-pane--embed">
          <FarmerPayments embedded />
        </div>
      )}
      {activeTab === "overview" && (
        <div className="fs-pane">
          <section className="fs-panel">
            <div className="fs-panel-head">
              <div>
                <div className="fs-panel-title">
                  <ShopOutlined />
                  {t("farmerSummaryTitle", "Farmer Finance")}
                </div>
                <div className="fs-panel-sub">
                  {t("farmerSummarySubtitle", "Filter farmer, products and dates, then build the payment report")}
                </div>
              </div>
              <Space wrap>
                {vendorId && (
                  <Link to={`/vendors/${vendorId}`}>
                    <Button icon={<EyeOutlined />}>{t("vendorOpenAccount", "Full account")}</Button>
                  </Link>
                )}
                {canAddExpenses && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setAddExpenseOpen(true)}
                  >
                    {t("vendorAddExpense", "Add expense")}
                  </Button>
                )}
                {canPayAndPrint && (
                  <Button
                    className="fs-print-btn"
                    icon={<PrinterOutlined />}
                    onClick={openSettlement}
                    loading={printing || settling || (orderScoped && loadingScoped)}
                    disabled={!report || !hasActivity || (orderScoped && (loadingScoped || !scopedReport))}
                  >
                    {t("printAndPay", "Pay & print")}
                  </Button>
                )}
              </Space>
            </div>

            <div className="fs-filter-body">
              <div className="fs-filter-row">
                <div>
                  <div className="fs-field-label">{t("fromDateTime", "From date")} ({t("optional", "optional")})</div>
                  <DatePicker
                    showTime={{ format: "HH:mm" }}
                    format="MM/DD/YYYY hh:mm A"
                    value={range?.[0]}
                    onChange={(value) => changeRangeBoundary(0, value)}
                    allowClear
                    needConfirm={false}
                    style={{ width: "100%" }}
                  />
                </div>
                <div>
                  <div className="fs-field-label">{t("toDateTime", "To date")} ({t("optional", "optional")})</div>
                  <DatePicker
                    showTime={{ format: "HH:mm" }}
                    format="MM/DD/YYYY hh:mm A"
                    value={range?.[1]}
                    onChange={(value) => changeRangeBoundary(1, value)}
                    allowClear
                    needConfirm={false}
                    style={{ width: "100%" }}
                  />
                </div>
                <div>
                  <div className="fs-field-label">{t("farmerLabel", "Farmer")} *</div>
                  <Select
                    showSearch
                    allowClear
                    loading={loadingBase}
                    value={vendorId}
                    onChange={chooseVendor}
                    optionFilterProp="label"
                    placeholder={t("vendorSelectFarmer", "Select a farmer")}
                    style={{ width: "100%" }}
                    options={vendors.map((vendor) => ({
                      value: vendor.id,
                      label: [
                        vendor.name,
                        vendor.phone,
                        vendor.consignment_owed != null
                          ? `(${formatCurrency(vendor.consignment_owed, mainCode)})`
                          : null,
                      ].filter(Boolean).join(" — "),
                    }))}
                  />
                </div>
                <div>
                  <div className="fs-field-label">{t("product", "Product")}</div>
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    maxTagCount="responsive"
                    value={productIds}
                    disabled={!vendorId}
                    optionFilterProp="label"
                    placeholder={t("allProducts", "All products")}
                    style={{ width: "100%" }}
                    onChange={setProductIds}
                    options={productOptions.map((product) => ({
                      value: product.product_id,
                      label: parseLocalizedName(product.name, i18n.language, `#${product.product_id}`),
                    }))}
                  />
                </div>
                <Button
                  className="fs-search-btn"
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={refresh}
                  disabled={!vendorId}
                  loading={loadingReport}
                >
                  {t("buildFarmerReport", "Show summary")}
                </Button>
              </div>

              <div
                className="fs-presets"
                style={{ alignItems: "center", display: presetOptions.length ? "flex" : "none" }}
              >
                {presetOptions.map((preset) => (
                  <Button
                    key={preset.value}
                    size="small"
                    type={presetKey === preset.value ? "primary" : "default"}
                    onClick={() => applyPreset(preset.value)}
                    style={{ borderRadius: 8 }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
          </section>

          {!vendorId ? (
            <div className="fs-empty">
              <Empty description={t("selectFarmerToViewReport", "Select a farmer and press Show summary")} />
            </div>
          ) : (
            <Spin spinning={loadingReport || (orderScoped && loadingScoped)}>
              <div className={`fs-hero ${remaining > 0 ? "" : "fs-hero--clear"}`}>
                <div>
                  <div className="fs-hero-label">
                    {orderScoped
                      ? t("amountToPaySelectedSales", "Amount to pay (selected sales)")
                      : t("amountToPayFarmer", "Amount to pay farmer")}
                  </div>
                  <div className="fs-hero-value">{formatCurrency(remaining, mainCode)}</div>
                  <div className="fs-hero-formula">
                    {formatCurrency(netOwed, mainCode)} ({t("netUnpaidShort", "net unpaid")})
                    {" − "}
                    {formatCurrency(paid, mainCode)} ({t("alreadyPaidShort", "already paid")})
                    {" = "}
                    {formatCurrency(remaining, mainCode)}
                  </div>
                </div>
              </div>

              {orderScoped ? (
                <div className="fs-selection-banner">
                  <span>
                    {t(
                      "farmerSaleSelectionHint",
                      "Clearing {{count}} selected sale(s). Shared farmer charges and earlier payments stay on the open account.",
                      { count: selectedSaleIds.length }
                    )}
                  </span>
                  <Button type="link" size="small" onClick={() => setSelectedSaleIds([])}>
                    {t("clearSaleSelection", "Clear selection")}
                  </Button>
                </div>
              ) : canPayAndPrint && openSaleIds.length > 1 ? (
                <div className="fs-selection-banner fs-selection-banner--muted">
                  {t(
                    "farmerSaleSelectionTip",
                    "Tip: tick sales below to clear one before another (same product / same day is fine)."
                  )}
                </div>
              ) : null}

              <div className="fs-kpi-grid">
                <Kpi
                  label={t("vendorNetQtySold", "Qty sold (net)")}
                  value={physicalTotals.qty.toLocaleString()}
                />
                <Kpi
                  label={t("vendorNetWeightSold", "Weight sold (net)")}
                  value={`${physicalTotals.weight.toLocaleString()} ${t("kg", "kg")}`}
                />
                <Kpi label={t("kpiTotalFare", "Total sales")} value={formatCurrency(soldTotal, mainCode)} />
                <Kpi label={t("kpiPaidAmount", "Already paid")} value={formatCurrency(paid, mainCode)} />
                <Kpi tone="green" label={t("kpiUnpaidFare", "Net unpaid")} value={formatCurrency(netOwed, mainCode)} />
                {canViewExpenses && (
                  <Kpi tone="orange" label={t("kpiUnpaidCharges", "Unpaid charges")} value={formatCurrency(expensesTotal, mainCode)} />
                )}
                <Kpi tone="green" label={t("kpiUnpaidReward", "Unpaid reward")} value={formatCurrency(activeReport?.total_returned, mainCode)} />
                <Kpi tone="red" label={t("netToPay", "NET TO PAY")} value={formatCurrency(remaining, mainCode)} />
              </div>

              <div className="fs-detail-farmer-bar">
                <div className="fs-detail-farmer">
                  <TeamOutlined />
                  {selectedVendor?.name || "—"}
                </div>
                <div className="fs-detail-period">
                  {range?.[0]?.format("YYYY-MM-DD HH:mm") || t("periodStart", "Beginning")}
                  {" → "}
                  {range?.[1]?.format("YYYY-MM-DD HH:mm") || t("periodNow", "Now")}
                </div>
              </div>

              <section className="fs-panel fs-panel--table">
                <div className="fs-detail-block">
                  <div className="fs-detail-main">
                    <div className="fs-section-bar">
                      <span>
                        <ShopOutlined />
                        {t("itemsSold", "Items")} ({saleItemRows.length})
                        {orderScoped
                          ? ` · ${t("selectedSalesCount", "{{count}} sale(s) selected", { count: selectedSaleIds.length })}`
                          : ""}
                      </span>
                    </div>
                    <Table
                      size="small"
                      rowSelection={saleRowSelection}
                      pagination={{ ...saleItemsPagination, size: "small" }}
                      dataSource={saleItemRows}
                      columns={saleColumns}
                      scroll={{ x: 1080 }}
                      locale={{ emptyText: t("vendorNoConsignment", "No sales in this period") }}
                    />
                  </div>
                  <div className="fs-detail-side">
                    <div className="fs-side-label">{t("netUnpaid", "Net unpaid")}</div>
                    <div className="fs-side-value fs-side-value--green">{formatCurrency(netOwed, mainCode)}</div>
                  </div>
                </div>
              </section>

              {canViewExpenses && (
              <section className="fs-panel fs-panel--table">
                <div className="fs-detail-block">
                  <div className="fs-detail-main">
                    <div className="fs-section-bar fs-section-bar--charges">
                      <span>
                        <ThunderboltOutlined />
                        {t("unpaidCharges", "Unpaid charges")} ({expenseRows.length})
                        {selectedExpenseIds.length
                          ? ` · ${t("selectedExpensesCount", "{{count}} charge(s) selected", { count: selectedExpenseIds.length })}`
                          : ""}
                      </span>
                      {selectedExpenseIds.length > 0 && (
                        <Button type="link" size="small" onClick={() => setSelectedExpenseIds([])}>
                          {t("clearExpenseSelection", "Clear selection")}
                        </Button>
                      )}
                    </div>
                    <Table
                      size="small"
                      rowSelection={expenseRowSelection}
                      pagination={{ ...expensePagination, size: "small" }}
                      dataSource={expenseRows}
                      columns={expenseColumns}
                      scroll={{ x: 720 }}
                      locale={{ emptyText: t("vendorNoExpenses", "No charges in this period") }}
                    />
                  </div>
                  <div className="fs-detail-side">
                    <div className="fs-side-label">{t("unpaidCharges", "Unpaid charges")}</div>
                    <div className="fs-side-value fs-side-value--orange">
                      −{formatCurrency(expensesTotal, mainCode)}
                    </div>
                  </div>
                </div>
              </section>
              )}
            </Spin>
          )}

          <Modal
            open={settleOpen}
            className="fs-settle-modal"
            rootClassName="fs-settle-modal-root"
            width={560}
            title={
              <span className="fs-settle-modal-title">
                {orderScoped
                  ? `${t("vendorClearSelectedSales", "Pay & clear selected")} (${selectedSaleIds.length}) — ${selectedVendor?.name || ""}`
                  : `${t("vendorClearAccount", "Pay & clear")} — ${selectedVendor?.name || ""}`}
              </span>
            }
            okText={
              remaining > 0.01 && money(settleAmount) < remaining - 0.01
                ? t("vendorPayPartial", "Record partial payment")
                : t("vendorClearAndArchive", "Clear & archive")
            }
            onCancel={() => setSettleOpen(false)}
            onOk={submitSettlement}
            confirmLoading={settling}
            destroyOnHidden
          >
            <Space direction="vertical" size={14} style={{ width: "100%" }}>
              <div className="fs-settle-remaining">
                <div className="fs-settle-remaining-label">{t("vendorRemainingToClear", "Remaining to clear")}</div>
                <div className="fs-settle-remaining-value">{formatCurrency(remaining, mainCode)}</div>
              </div>

              <div className="fs-settle-preview">
                <div className="fs-settle-preview-title">
                  {t("settlePreviewTitle", "Clear preview")}
                </div>
                <div className="fs-settle-preview-grid">
                  <div className="fs-settle-preview-item">
                    <span>{t("settlePreviewScope", "Scope")}</span>
                    <b>
                      {settlePreview.orderScoped
                        ? t("settlePreviewSelectedSales", "{{count}} selected sale(s)", { count: settlePreview.saleCount })
                        : t("settlePreviewFilteredPeriod", "All sales in current filter")}
                    </b>
                  </div>
                  <div className="fs-settle-preview-item">
                    <span>{t("settlePreviewAction", "Action")}</span>
                    <b className={settlePreview.willArchive ? "fs-settle-preview-ok" : "fs-settle-preview-warn"}>
                      {settlePreview.willArchive
                        ? t("settlePreviewWillArchive", "Pay & archive")
                        : t("settlePreviewPartialOnly", "Partial pay — account stays open")}
                    </b>
                  </div>
                  <div className="fs-settle-preview-item">
                    <span>{t("vendorNetQtySold", "Qty sold (net)")}</span>
                    <b>{settlePreview.qty.toLocaleString()}</b>
                  </div>
                  <div className="fs-settle-preview-item">
                    <span>{t("vendorNetWeightSold", "Weight sold (net)")}</span>
                    <b>{`${settlePreview.weight.toLocaleString()} ${t("kg", "kg")}`}</b>
                  </div>
                  <div className="fs-settle-preview-item">
                    <span>{t("vendorGross", "Gross")}</span>
                    <b>{formatCurrency(settlePreview.gross, mainCode)}</b>
                  </div>
                  <div className="fs-settle-preview-item">
                    <span>{t("unpaidCharges", "Unpaid charges")}</span>
                    <b>−{formatCurrency(settlePreview.charges, mainCode)}</b>
                  </div>
                  <div className="fs-settle-preview-item">
                    <span>{t("kpiPaidAmount", "Already paid")}</span>
                    <b>{formatCurrency(settlePreview.paid, mainCode)}</b>
                  </div>
                  <div className="fs-settle-preview-item fs-settle-preview-item--strong">
                    <span>{t("settlePreviewPayNow", "You will pay now")}</span>
                    <b>{formatCurrency(settlePreview.payNow, mainCode)}</b>
                  </div>
                </div>

                {settlePreview.saleDocs.length > 0 && (
                  <div className="fs-settle-preview-sales">
                    <div className="fs-settle-preview-sales-label">
                      {t("settlePreviewSalesList", "Sales included")}
                    </div>
                    <div className="fs-settle-preview-chips">
                      {settlePreview.saleDocs.slice(0, 8).map((doc) => (
                        <span key={doc} className="fs-settle-preview-chip">{doc}</span>
                      ))}
                      {settlePreview.saleDocs.length > 8 && (
                        <span className="fs-settle-preview-chip fs-settle-preview-chip--more">
                          +{settlePreview.saleDocs.length - 8}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="fs-settle-preview-note">
                  {settlePreview.orderScoped
                    ? t(
                      "settlePreviewOrderScopedHint",
                      "Shared farmer charges and earlier account payments stay open. Only the selected sales are archived when you clear in full."
                    )
                    : settlePreview.willArchive
                      ? t(
                        "settlePreviewFullHint",
                        "Full clear archives these sales and the charges shown above, then prints the settlement."
                      )
                      : t(
                        "settlePreviewPartialHint",
                        "Partial payment records cash only — sales and charges stay open until you pay the full remaining."
                      )}
                </div>
              </div>

              <div>
                <div className="fs-settle-field-label">{t("amount", "Amount")} ({mainCode})</div>
                <InputNumber
                  min={0}
                  max={remaining > 0 ? remaining : undefined}
                  value={settleAmount}
                  onChange={(value) => setSettleAmount(value ?? 0)}
                  className="fs-settle-input"
                  style={{ width: "100%", marginTop: 6 }}
                />
              </div>
              {remaining > 0 && (
                <Button type="link" className="fs-settle-full-btn" onClick={() => setSettleAmount(remaining)}>
                  {t("vendorsPayFullBalance", "Pay full remaining")}
                </Button>
              )}
              <div>
                <div className="fs-settle-field-label">{t("note", "Note")}</div>
                <Input.TextArea
                  value={settleNote}
                  onChange={(event) => setSettleNote(event.target.value)}
                  rows={3}
                  className="fs-settle-note"
                  style={{ marginTop: 6 }}
                />
              </div>
            </Space>
          </Modal>

          {canAddExpenses && (
            <AddVendorExpenseModal
              open={addExpenseOpen}
              onClose={() => setAddExpenseOpen(false)}
              initialVendorId={vendorId}
              vendors={vendors}
              onSuccess={async (expenseVendorId) => {
                await refreshVendors();
                if (vendorId && Number(expenseVendorId) === Number(vendorId)) {
                  await loadReport(vendorId, productIds, range);
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ tone, label, value }) {
  return (
    <div className={`fs-kpi${tone ? ` fs-kpi--${tone}` : ""}`}>
      <div className="fs-kpi-label">{label}</div>
      <div className="fs-kpi-value">{value}</div>
    </div>
  );
}
